import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px);
  overflow-y: auto;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px);
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
`;

const ProgressBarContainer = styled.div`
  width: 50vw;
  max-width: 280px;
  margin: 0 auto 32px auto;
  height: 4px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => (props.progress / props.total) * 100}%;
  background: ${theme.colors.doryGreen};
  border-radius: 2px;
  transition: width ${theme.animation.durations.normal};
`;

const Title = styled.h1`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
  text-wrap: balance;
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.relaxed};
  margin: 0 0 ${theme.spacing.lg} 0;
  text-wrap: balance;
`;

const NameInput = styled.input`
  width: 100%;
  max-width: 300px;
  padding: ${theme.spacing.inputPadding};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  background: white;
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
  transition: border-color ${theme.animation.durations.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  &::placeholder {
    color: ${theme.colors.textLight};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  width: 100%;
  margin-top: ${theme.spacing.md};
`;

const PrimaryButton = styled.button`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.semiBold};
  min-height: 52px;
  width: 100%;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent});
  color: #fff;
  cursor: pointer;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: #8b4a4a;
  margin: 0 0 ${theme.spacing.md} 0;
`;

const FirstNameScreen = ({ data, onResponse, userProgress = {} }) => {
  const content = data?.content || {};
  const htmlIntro = content.text
    ? formatTextContent(content.text, userProgress)
    : null;

  const existingName =
    typeof userProgress?.userInfo?.name === 'string'
      ? userProgress.userInfo.name.trim()
      : '';

  const [firstName, setFirstName] = useState(existingName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const title = content.question || "What's your name?";
  const placeholder = content.placeholder || 'Your name';
  const trimmed = firstName.trim();

  const finishAndSubmit = async () => {
    if (isSubmitting || !trimmed) return;
    setIsSubmitting(true);
    setError('');

    const responseData = {
      checkpointKey: data.key,
      type: 'first_name',
      formData: {
        name: trimmed,
      },
      timestamp: Date.now(),
    };

    try {
      await onResponse?.(responseData);
    } catch (err) {
      console.error('Failed to save first name response:', err);
      setError('Failed to save your name. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && trimmed && !isSubmitting) {
      e.preventDefault();
      finishAndSubmit();
    }
  };

  return (
    <Container>
      <Content>
        <ProgressBarContainer>
          <ProgressFill progress={1} total={1} />
        </ProgressBarContainer>

        {htmlIntro && (
          <Description dangerouslySetInnerHTML={{ __html: htmlIntro }} />
        )}

        <Title>{title}</Title>

        <NameInput
          type="text"
          autoComplete="given-name"
          placeholder={placeholder}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="First name"
        />

        {error && <ErrorText>{error}</ErrorText>}

        <ButtonGroup>
          <PrimaryButton
            onClick={finishAndSubmit}
            disabled={isSubmitting || !trimmed}
          >
            {isSubmitting ? 'Saving…' : content.ctaText || 'Continue'}
          </PrimaryButton>
        </ButtonGroup>
      </Content>
    </Container>
  );
};

export default FirstNameScreen;
